import { useState } from 'react'
import { stepCode } from '../../../lib/curl.js'
import { refMatches } from '../buildAnswer.js'
import { useWidgetContext } from './context.js'
import {
  AttachedGotchas,
  Card,
  Code,
  CopyButton,
  PendingLine,
  SystemChip,
  Watch,
  asList,
  systemName,
  useSystemStyle,
} from './primitives.jsx'
import w from './widgets.module.css'

const KIND_LABEL = { request: 'Request', payload: 'Payload', note: 'Step' }

/** #2 Two-system handoff: numbered timeline, one step open at a time. */
export function Handoff({ input, block }) {
  const { systemsMap } = useWidgetContext()
  const systemStyle = useSystemStyle()
  const steps = asList(input.steps)
  const ids = asList(input.travelling_ids).filter((t) => t.from && t.to)
  const [open, setOpen] = useState(0)

  return (
    <Card
      label={input.title ?? 'Handoff'}
      title={input.title ?? 'Handoff'}
      right={
        <>
          <SystemChip slug={input.source_system} />
          <span className={w.muted}>→</span>
          <SystemChip slug={input.target_system} />
        </>
      }
    >
      {steps.length ? (
        <ol className={w.tl}>
          {steps.map((step, i) => (
            <li key={`${step.op_id}-${i}`} className={w.tlItem} style={systemStyle(step.system)}>
              <div className={w.tlRail}>
                <span className={w.tlNum}>{i + 1}</span>
              </div>
              <div className={`${w.tlCard} ${i === open ? w.tlOpen : ''}`}>
                <button
                  type="button"
                  className={w.tlHead}
                  aria-expanded={i === open}
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  <span className={w.tlSys}>{systemName(systemsMap, step.system)}</span>
                  <span className={w.tlLabel}>{step.label}</span>
                  <span className={w.tlOp}>{step.op_id}</span>
                  <span className={w.tlMore}>
                    {i === open ? 'Hide' : step.kind === 'payload' ? 'Show payload' : step.kind === 'note' ? 'Show' : 'Show request'}
                    <span className={w.chev} aria-hidden="true">
                      ›
                    </span>
                  </span>
                </button>
                {i === open ? (
                  <StepBody step={step} gotchas={asList(block.gotchas).filter((g) => refMatches(g.attach_to?.ref, step.op_id))} />
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <PendingLine />
      )}
      {ids.length ? (
        <div className={`${w.row} ${w.muted}`}>
          Ids that travel:
          {ids.map((t) => (
            <span key={`${t.from}-${t.to}`} className={w.idPill}>
              {t.from} → {t.to}
            </span>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

function StepBody({ step, gotchas }) {
  const code = stepCode(step)
  const kind = KIND_LABEL[step.kind] ?? 'Request'
  const checks = asList(step.checks)
  const keep = asList(step.keep)
  return (
    <div className={w.tlBody}>
      {code ? (
        <div className={w.colTight}>
          <div className={w.row}>
            <span className={`section-label ${w.grow}`}>{kind}</span>
            <CopyButton
              text={code}
              label={step.kind === 'payload' ? 'Copy payload' : 'Copy cURL'}
            />
          </div>
          <Code code={code} />
        </div>
      ) : null}
      {checks.length ? (
        <div className={w.colTight}>
          <span className="section-label">Check</span>
          <ul className={w.plainList}>
            {checks.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {keep.length ? (
        <div className={w.colTight}>
          <span className="section-label">Keep</span>
          <div className={w.rowTight}>
            {keep.map((k) => (
              <span key={k} className={w.idPill}>
                {k}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {step.watch ? <Watch>{step.watch}</Watch> : null}
      <AttachedGotchas items={gotchas} />
    </div>
  )
}
